import classNames from 'classnames'
import React, { forwardRef, HTMLProps, ReactElement, ReactNode, useId, useState } from 'react'
import { useStableId } from '../../utils'
import { FormError } from '../form-error'
import { IconFa } from '../icon-fa'
import styles from './index.module.css'

export interface Props extends Omit<HTMLProps<HTMLInputElement>, 'label'> {
  // An icon component to show on the left.
  icon?: ReactNode
  // A help text tooltip to show on the right.
  help?: string
  // An icon component to show instead of the default help icon.
  helpIcon?: ReactNode
  // A clear text tooltip to show on the right.
  clear?: string
  // Pass an onHelp click handler to show a help button.
  onHelp?: () => void
  // Pass an onClear click handler to show a clear button.
  onClear?: () => void
  errorMessage?: ReactNode
  label?: ReactNode
  hideLabel?: boolean
  descriptionText?: string
  requiredText?: string
  // Used for show/hide password
  showPassword?: boolean
  inputClassName?: string
  errorClassName?: string
  onShowHidePassword?: () => void
}

export const FormInput = forwardRef<HTMLInputElement, Props>(function FormInput(props, ref): ReactElement {
  const {
    id,
    icon,
    help,
    hideLabel,
    label,
    errorMessage,
    onHelp,
    onClear,
    descriptionText,
    requiredText = '*',
    required,
    onShowHidePassword,
    showPassword,
    inputClassName: outerInputClassName,
    errorClassName,
    ...rest
  } = props
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const stableId = useStableId()
  const errorMessageElId = useId()
  const inputId = id ? id : stableId

  const inputClassName = classNames(
    styles.input,
    outerInputClassName,
    !!icon && styles.hasLeftIcon,
    (help || onHelp || onClear) && styles.hasRightIcon
  )

  const labelClassName = classNames(
    styles.label,
    Boolean(props.value) && styles.hasValue,
    props['aria-invalid'] && styles.hasError,
    props.disabled && styles.disabled,
    props.className
  )

  const labelTextClassName = classNames(
    styles.labelText,
    isFocused && styles.focus,
    props['aria-invalid'] && styles.hasError,
    descriptionText && styles.bold
  )

  const onFocus = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    if (props.onFocus) {
      props.onFocus(event)
    }

    setIsFocused(true)
  }

  const onBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    props.onBlur?.(event)
    setIsFocused(false)
  }

  const triggerFocusOnIconClick = () => {
    const inputEl = document.getElementById(inputId)
    if (inputEl) {
      inputEl.focus()
    }
  }

  const showClearIcon = onClear && props.value

  return (
    <div className={labelClassName}>
      {!hideLabel && label && (
        <label className={labelTextClassName} data-focused={isFocused} htmlFor={inputId}>
          {label}
          {required && requiredText}
        </label>
      )}
      {descriptionText && <div className={styles.descriptionText}>{descriptionText}</div>}
      <div className="relative">
        {icon && (
          // biome-ignore lint/a11y/noStaticElementInteractions: as before
          // biome-ignore lint/a11y/useKeyWithClickEvents: as before
          <div onClick={triggerFocusOnIconClick} className={styles.leftIcon}>
            {icon}
          </div>
        )}
        <input
          {...rest}
          aria-required={required}
          aria-describedby={errorMessageElId}
          data-focused={isFocused}
          id={inputId}
          ref={ref}
          onFocus={onFocus}
          onBlur={onBlur}
          className={inputClassName}
        />
        {showClearIcon && <ClearIcon {...props} />}
        {!showClearIcon && (help || onHelp) && <HelpIcon {...props} />}
        {onShowHidePassword && <ShowHideIcon showPassword={showPassword} {...props} />}
      </div>
      <FormError
        className={errorClassName}
        id={errorMessageElId}
        showError={!!props['aria-invalid']}
        errorMessage={errorMessage}
      />
    </div>
  )
})

function HelpIcon(props: Props) {
  if (props.onHelp) {
    return (
      <button type="button" className={styles.rightIcon} aria-label={props.help} onClick={props.onHelp}>
        {props.helpIcon ?? <IconFa icon={['far', 'info-circle']} title={props.help} />}
      </button>
    )
  }

  return (
    <div className={styles.rightIcon} title={props.help}>
      {props.helpIcon ?? <IconFa icon={['far', 'info-circle']} />}
    </div>
  )
}

function ClearIcon(props: Props) {
  return (
    <button type="button" className={styles.rightIcon} aria-label={props.clear} onClick={props.onClear}>
      <IconFa icon={['far', 'times']} title={props.clear} />
    </button>
  )
}

function ShowHideIcon(props: Props) {
  return (
    <button type="button" className={styles.rightIcon} onClick={props.onShowHidePassword}>
      <IconFa icon={['far', props.showPassword ? 'eye-slash' : 'eye']} title={props.clear} />
    </button>
  )
}
